import { EditableSelect } from 'features/libra/components/EditableSelect'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import {
  createFieldSetter,
  createFieldSetterWithDefault,
} from 'features/libra/stepComponents/niiReport/createFieldSetter'
import { EditableNotes } from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { confidenceValues } from 'features/libra/stepComponents/niiScreening/niiScreeningApi'
import { useNiiScreeningData } from 'features/libra/stepComponents/niiScreening/NiiScreeningDataContext'
import React, { FC, useMemo } from 'react'

const confidenceOptions = [
  { value: 'high', label: 'High', color: 'green' },
  { value: 'med', label: 'Med', color: 'orange' },
  { value: 'low', label: 'Low', color: 'red' },
] as const satisfies ReadonlyArray<{
  value: (typeof confidenceValues)[number]
  label: string
  color: string
}>

export const RawNiiSeverityAndRiskCard: FC<{ readonly?: boolean }> = ({
  readonly,
}) => {
  const { niiScreening, setNiiScreening, isLoading } = useNiiScreeningData()

  const [
    setConfidenceInPredictingDegradationTypeAndLocation,
    setConfidenceInPredictingDegradationTypeAndLocationJustification,
    setPreviousInspectionEffectiveness,
    setPreviousInspectionEffectivenessJustification,
    setSeverityAndRateOfDegradation,
    setSeverityAndRateOfDegradationJustification,
  ] = useMemo(
    () => [
      createFieldSetterWithDefault(
        'confidenceInPredictingDegradationTypeAndLocation',
        setNiiScreening,
        'med'
      ),
      createFieldSetter(
        'confidenceInPredictingDegradationTypeAndLocationJustification',
        setNiiScreening
      ),
      createFieldSetterWithDefault(
        'previousInspectionEffectiveness',
        setNiiScreening,
        'med'
      ),
      createFieldSetter(
        'previousInspectionEffectivenessJustification',
        setNiiScreening
      ),
      createFieldSetterWithDefault(
        'severityAndRateOfDegradation',
        setNiiScreening,
        'med'
      ),
      createFieldSetter(
        'severityAndRateOfDegradationJustification',
        setNiiScreening
      ),
    ],
    [setNiiScreening]
  )

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Confidence in ability to predict type and location of degradation',
        <EditableSelect
          key='confidenceInPredictingDegradationTypeAndLocation'
          value={niiScreening?.confidenceInPredictingDegradationTypeAndLocation}
          onChange={
            readonly
              ? undefined
              : (v) => setConfidenceInPredictingDegradationTypeAndLocation(v)
          }
          options={confidenceOptions}
        />,
      ] as const,
      [
        'Confidence in ability to predict type and location of degradation: justification',
        <EditableNotes
          key='confidenceInPredictingDegradationTypeAndLocationJustification'
          value={
            niiScreening?.confidenceInPredictingDegradationTypeAndLocationJustification
          }
          onChange={
            readonly
              ? undefined
              : setConfidenceInPredictingDegradationTypeAndLocationJustification
          }
        />,
      ] as const,
      [
        'Previous inspection effectiveness',
        <EditableSelect
          key='previousInspectionEffectiveness'
          value={niiScreening?.previousInspectionEffectiveness}
          onChange={
            readonly ? undefined : (v) => setPreviousInspectionEffectiveness(v)
          }
          options={confidenceOptions}
        />,
      ] as const,
      [
        'Previous inspection effectiveness: justification',
        <EditableNotes
          key='previousInspectionEffectivenessJustification'
          value={niiScreening?.previousInspectionEffectivenessJustification}
          onChange={
            readonly
              ? undefined
              : setPreviousInspectionEffectivenessJustification
          }
        />,
      ] as const,
      [
        'Severity and rate of degradation',
        <EditableSelect
          key='severityAndRateOfDegradation'
          value={niiScreening?.severityAndRateOfDegradation}
          onChange={
            readonly ? undefined : (v) => setSeverityAndRateOfDegradation(v)
          }
          options={confidenceOptions}
        />,
      ] as const,
      [
        'Severity and rate of degradation: justification',
        <EditableNotes
          key='severityAndRateOfDegradationJustification'
          value={niiScreening?.severityAndRateOfDegradationJustification}
          onChange={
            readonly ? undefined : setSeverityAndRateOfDegradationJustification
          }
        />,
      ] as const,
    ],
    [
      niiScreening?.confidenceInPredictingDegradationTypeAndLocation,
      niiScreening?.confidenceInPredictingDegradationTypeAndLocationJustification,
      niiScreening?.previousInspectionEffectiveness,
      niiScreening?.previousInspectionEffectivenessJustification,
      niiScreening?.severityAndRateOfDegradation,
      niiScreening?.severityAndRateOfDegradationJustification,
      setConfidenceInPredictingDegradationTypeAndLocation,
      setConfidenceInPredictingDegradationTypeAndLocationJustification,
      setPreviousInspectionEffectiveness,
      setPreviousInspectionEffectivenessJustification,
      setSeverityAndRateOfDegradation,
      setSeverityAndRateOfDegradationJustification,
      readonly,
    ]
  )

  return (
    <StyledCard title='NII Screening Logic'>
      <OptionalDescriptions items={items} isLoading={isLoading} />
    </StyledCard>
  )
}

export const NiiSeverityAndRiskCard = React.memo(RawNiiSeverityAndRiskCard)
